<template>
  <div class="f-main bgImg container billing-page">
    <div class="box">
      <header class="hero-banner">
        <div class="hero-main">
          <div class="hero-left">
            <div class="hero-icon-box">
              <i class="el-icon-wallet" aria-hidden="true" />
            </div>
            <div class="hero-text">
              <div class="hero-tags">
                <span class="hero-tag">{{ $t('billingPage.billing') }}</span>
                <span class="hero-tag hero-tag-active">{{ $t('billingPage.annualLicense') }}</span>
              </div>
              <h1 class="hero-title">{{ $t('billingPage.billing') }}</h1>
              <p class="hero-sub">{{ $t('billingPage.pageSubtitle') }}</p>
            </div>
          </div>
          <div class="hero-right">
            <div class="hero-summary">
              <div class="hero-stat">
                <span class="hero-stat-num">CAD</span>
                <span class="hero-stat-label">{{ $t('billingPage.currency') }}</span>
              </div>
              <div class="hero-stat">
                <span class="hero-stat-num">{{ invoices.length }}</span>
                <span class="hero-stat-label">{{ $t('billingPage.invoices') }}</span>
              </div>
            </div>
            <div class="hero-actions">
              <button type="button" class="hero-home-btn" @click="goHome">
                <i class="el-icon-house" aria-hidden="true" />
                <span>{{ $t('billingPage.home') }}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="main-content">

        <section class="card-section product-section">
          <div v-if="products.length > 0" class="section-title-row">
            <h3 class="section-pill-title">{{ $t('billingPage.annualLicense') }}</h3>
            <span class="billing-count">{{ $t('billingPage.annualAccessNote') }}</span>
          </div>
          <div v-if="loading" class="empty-state">{{ $t('billingPage.loadingBilling') }}</div>
          <div v-else-if="!loading && products.length === 0" class="empty-state product-empty-state">
            <p>{{ $t('billingPage.noProducts') }}</p>
          </div>
          <div v-else-if="activeProducts.length === 0" class="empty-state product-empty-state">
            <p>{{ $t('billingPage.noAnnualProducts') }}</p>
          </div>
          <div v-if="activeProducts.length" class="annual-product-grid product-list-wrap">
            <article v-for="product in activeProducts" :key="product.id" class="annual-product-card">
              <div class="annual-card-main">
                <div class="annual-card-copy">
                  <div class="annual-card-badges">
                    <span class="product-type-badge">{{ product.typeLabel }}</span>
                    <span class="product-status-badge">{{ product.statusLabel }}</span>
                  </div>
                  <h4>{{ product.name }}</h4>
                  <p v-if="product.description">{{ product.description }}</p>
                </div>
                <div class="annual-price-hero">
                  <span>{{ $t('billingPage.startingAt') }}</span>
                  <strong>{{ product.bestPrice }}</strong>
                  <small>{{ $t('billingPage.perStudentYear') }}</small>
                </div>
              </div>
              <div class="annual-tier-row">
                <div
                  v-for="tier in product.priceTiers"
                  :key="tier.label"
                  class="annual-tier-card"
                >
                  <span>{{ tier.label }}</span>
                  <strong>{{ tier.price }}</strong>
                  <small v-if="tier.note">{{ tier.note }}</small>
                </div>
              </div>
              <div class="annual-card-footer">
                <span>{{ product.savingsLabel }}</span>
                <button
                  type="button"
                  class="product-action-btn annual-buy-btn"
                  :disabled="actionLoadingProductId === product.id"
                  @click="openPurchaseDialog(product)"
                >
                  <span>{{ product.actionLabel }}</span>
                  <i class="el-icon-arrow-right" aria-hidden="true" />
                </button>
              </div>
            </article>
          </div>
        </section>

        <section v-if="isTeacher" class="card-section teacher-students-section">
          <div class="section-title-row">
            <h3 class="section-pill-title">{{ $t('billingPage.licenseUsage') }}</h3>
            <span class="billing-count">{{ $t('billingPage.licenseSummaryStats', { purchased: totalPurchasedSeats, assigned: totalUsedSeats, available: totalAvailableSeats }) }}</span>
          </div>
          <div v-if="seatUsageLoading" class="empty-state">{{ $t('billingPage.loadingLicenseUsage') }}</div>
          <div v-else-if="licenseUsageOrders.length === 0" class="empty-state product-empty-state">
            <p>{{ $t('billingPage.noPurchasedSeats') }}</p>
          </div>
          <div v-else class="license-usage-list">
            <article v-for="usage in licenseUsageOrders" :key="usage.orderNo" class="license-usage-card">
              <div class="license-usage-head">
                <div>
                  <strong>{{ usage.orderNo }}</strong>
                  <span>{{ usage.productName }} · {{ usage.date }}</span>
                </div>
                <span class="license-usage-pill">{{ $t('billingPage.orderUsage', { used: usage.used, total: usage.total, available: usage.available }) }}</span>
              </div>
              <div v-if="usage.students.length" class="activated-students-list">
                <span
                  v-for="student in usage.students"
                  :key="`${usage.orderNo}-${student.studentId}`"
                  class="activated-student-chip"
                >
                  {{ student.studentName || student.studentAccount || formatStudentFallback(student.studentId) }}
                </span>
              </div>
              <p v-else class="license-usage-empty">{{ $t('billingPage.noStudentsActivatedFromOrder') }}</p>
            </article>
          </div>
        </section>

        <section v-if="isTeacher" class="card-section teacher-students-section">
          <div class="section-title-row">
            <h3 class="section-pill-title">{{ $t('billingPage.linkedStudents') }}</h3>
            <span class="billing-count">{{ $t('billingPage.studentsAssignedSummary', { active: activatedTeacherStudents.length, assigned: teacherStudents.length }) }}</span>
          </div>
          <div v-if="studentsLoading" class="empty-state">{{ $t('billingPage.loadingLinkedStudents') }}</div>
          <div v-else-if="teacherStudents.length === 0" class="empty-state product-empty-state">
            <p>{{ $t('billingPage.noLinkedStudents') }}</p>
          </div>
          <div v-else class="student-license-list">
            <div
              v-for="student in teacherStudents"
              :key="student.studentId"
              :class="['student-license-row', { activated: isStudentActivated(student) }]"
            >
              <div class="student-license-main">
                <strong>{{ student.studentName || student.studentAccount || formatStudentFallback(student.studentId) }}</strong>
                <span>{{ formatStudentMeta(student) }}</span>
              </div>
              <span v-if="isStudentActivated(student)" class="student-activated-badge">
                <span>{{ formatActivatedText(student) }}</span>
                <small v-if="student.activatedOrderNo">{{ student.activatedOrderNo }}</small>
              </span>
              <button
                v-else
                type="button"
                class="product-action-btn"
                :disabled="activationLoadingStudentId === student.studentId"
                @click="activateStudentLicense(student)"
              >
                <i v-if="activationLoadingStudentId === student.studentId" class="el-icon-loading" aria-hidden="true" />
                {{ $t('billingPage.activateForStudent') }}
              </button>
            </div>
          </div>
        </section>

        <section class="card-section billing-history-section">
          <div class="section-title-row">
            <h3 class="section-pill-title">{{ $t('billingPage.billingHistory') }}</h3>
            <span class="billing-count">{{ $t('billingPage.invoicesCount', { count: invoices.length }) }}</span>
          </div>
          <div class="table-wrap">
            <table class="billing-table">
              <thead>
                <tr>
                  <th>{{ $t('billingPage.invoice') }}</th>
                  <th>{{ $t('billingPage.date') }}</th>
                  <th>{{ $t('billingPage.plan') }}</th>
                  <th>{{ $t('billingPage.card') }}</th>
                  <th>{{ $t('billingPage.amount') }}</th>
                  <th>{{ $t('billingPage.status') }}</th>
                </tr>
              </thead>
              <tbody v-if="invoices.length">
                <tr v-for="invoice in invoices" :key="invoice.id">
                  <td class="invoice-id">{{ invoice.id }}</td>
                  <td>{{ invoice.date }}</td>
                  <td>{{ invoice.plan }}</td>
                  <td>{{ invoice.card || $t('billingPage.defaultCard') }}</td>
                  <td class="amount">{{ invoice.amount }}</td>
                  <td><span :class="['status-tag', invoice.statusClass]">{{ invoice.status }}</span></td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="empty-table-cell">{{ $t('billingPage.noBillingHistory') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </main>
    </div>

    <el-dialog
      :title="$t('billingPage.confirmAnnualLicense')"
      :visible.sync="purchaseDialogVisible"
      width="520px"
      custom-class="annual-purchase-dialog"
      append-to-body
      :close-on-click-modal="actionLoadingProductId === ''"
    >
      <div v-if="selectedProduct" class="purchase-dialog-body">
        <div class="dialog-product-head">
          <span class="product-type-badge">{{ selectedProduct.typeLabel }}</span>
          <h4>{{ selectedProduct.name }}</h4>
          <p v-if="selectedProduct.description && !isStudent">{{ selectedProduct.description }}</p>
        </div>
        <div v-if="isTeacher" class="dialog-quantity-row">
          <div>
            <strong>{{ $t('billingPage.quantity') }}</strong>
            <span>{{ $t('billingPage.chooseSeats') }}</span>
          </div>
          <div class="quantity-control purchase-quantity-control">
            <button type="button" :disabled="dialogQuantity <= 1" @click="setLicenseQuantity(dialogQuantity - 1)">-</button>
            <input
              v-model.number="dialogQuantity"
              type="number"
              min="1"
              step="1"
              @change="handleQuantityChange"
            >
            <button type="button" @click="setLicenseQuantity(dialogQuantity + 1)">+</button>
          </div>
        </div>
        <div v-else class="dialog-quantity-row">
          <div>
            <strong>{{ $t('billingPage.quantity') }}</strong>
            <span>{{ $t('billingPage.studentFixedQuantity') }}</span>
          </div>
          <span class="quantity-fixed">1</span>
        </div>
        <div class="dialog-quote-card">
          <div>
            <span>{{ $t('billingPage.unitPrice') }}</span>
            <strong>{{ dialogUnitPrice }}</strong>
          </div>
          <div>
            <span>{{ $t('billingPage.total') }}</span>
            <strong>{{ dialogTotalPrice }}</strong>
          </div>
        </div>
        <div :class="['annual-tier-row', 'dialog-tier-row', { 'dialog-tier-row--single': isStudent }]">
          <div
            v-for="tier in dialogPriceTiers"
            :key="tier.label"
            class="annual-tier-card"
          >
            <span>{{ tier.label }}</span>
            <strong>{{ tier.price }}</strong>
            <small>{{ tier.note }}</small>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <button type="button" class="dialog-secondary-btn" @click="purchaseDialogVisible = false">{{ $t('billingPage.cancel') }}</button>
        <button
          type="button"
          class="product-action-btn dialog-primary-btn"
          :disabled="!selectedProduct || dialogQuoteLoading || actionLoadingProductId !== ''"
          @click="confirmAnnualLicensePurchase"
        >
          <i v-if="dialogQuoteLoading || actionLoadingProductId !== ''" class="el-icon-loading" aria-hidden="true" />
          {{ $t('billingPage.payWithDefaultCard') }}
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/http.js'

const STRIPE_JS_URL = 'https://js.stripe.com/v3/'
let stripeJsPromise = null

export default {
  name: 'billing',
  data () {
    return {
      currency: 'cad',
      loading: false,
      actionLoadingProductId: '',
      activationLoadingStudentId: '',
      quoteLoadingProductId: '',
      studentsLoading: false,
      seatUsageLoading: false,
      licenseQuantity: 1,
      dialogQuantity: 1,
      dialogQuote: null,
      dialogQuoteLoading: false,
      purchaseDialogVisible: false,
      selectedProduct: null,
      annualQuotes: {},
      annualTierQuotes: {},
      products: [],
      orders: [],
      seatUsage: null,
      userInfo: null,
      teacherStudents: []
    }
  },
  computed: {
    isStudent () {
      return this.userInfo?.role === 'S'
    },
    isTeacher () {
      return Boolean(this.userInfo) && !this.isStudent
    },
    activeProducts () {
      return this.annualLicenseProducts
    },
    activatedTeacherStudents () {
      return this.teacherStudents.filter(student => this.isStudentActivated(student))
    },
    totalPurchasedSeats () {
      return Number(this.seatUsage?.totalSeats || 0)
    },
    totalUsedSeats () {
      return Number(this.seatUsage?.usedSeats || 0)
    },
    totalAvailableSeats () {
      return Math.max(this.totalPurchasedSeats - this.totalUsedSeats, 0)
    },
    licenseUsageOrders () {
      const orders = this.seatUsage?.orders || []
      return orders.map((order) => {
        return {
          orderNo: order.orderNo || order.orderId,
          productName: this.localizedProductName(order, order.orderNo || order.orderId),
          date: this.formatDate(order.paidAt),
          total: Number(order.quantity || 0),
          used: Number(order.used || 0),
          available: Number(order.available || 0),
          students: order.activatedStudents || []
        }
      })
    },
    dialogUnitPrice () {
      const quote = this.dialogQuote || this.selectedProduct?.quote
      return quote
        ? this.formatMoney(quote.unitAmount, quote.currency)
        : this.selectedProduct?.standardPrice || this.$t('billingPage.loadingPrice')
    },
    dialogTotalPrice () {
      const quote = this.dialogQuote
      return quote
        ? this.formatMoney(quote.totalAmount, quote.currency)
        : this.$t('billingPage.loadingTotal')
    },
    dialogPriceTiers () {
      const tiers = this.selectedProduct?.priceTiers || []
      return this.isStudent ? tiers.slice(0, 1) : tiers
    },
    annualLicenseProducts () {
      return this.products
        .filter(product => product.productType === 'ANNUAL_LICENSE')
        .map((product) => {
          const quote = this.annualQuotes[product.id]
          const price = this.findAnnualLicenseBasePrice(product)
          return {
            id: product.id,
            priceId: quote?.priceId || price?.id,
            name: this.$t('billingPage.annualProductName'),
            description: this.localizedProductDescription(product) || this.$t('billingPage.defaultProductDescription'),
            quote,
            unitPrice: quote
              ? this.formatMoney(quote.unitAmount, quote.currency)
              : this.$t('billingPage.loadingPrice'),
            totalPrice: quote
              ? this.formatMoney(quote.totalAmount, quote.currency)
              : this.$t('billingPage.loadingTotal'),
            standardPrice: this.formatTierPrice(product.id, 1),
            bestPrice: this.formatTierPrice(product.id, 31),
            priceTiers: this.annualLicensePriceTiers(product.id),
            savingsLabel: this.isTeacher ? this.formatBestSavings(product.id) : this.$t('billingPage.studentPurchaseActivates'),
            quoteLoading: this.quoteLoadingProductId === product.id,
            typeLabel: this.$t('billingPage.annual'),
            statusLabel: quote ? this.$t('billingPage.cadQuoted') : 'CAD',
            actionLabel: this.$t('billingPage.purchaseLicenses'),
            defaultDescription: this.$t('billingPage.defaultProductDescription')
          }
        })
    },
    invoices () {
      return this.orders.map(order => ({
        id: order.orderNo || order.id,
        date: this.formatDate(order.paidAt || order.createAt),
        plan: this.localizedProductName(order, order.productId),
        card: '',
        amount: this.formatMoney(order.amount, order.currency || this.currency),
        status: this.formatOrderStatus(order.status),
        statusClass: this.getOrderStatusClass(order.status)
      }))
    }
  },
  mounted () {
    this.userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null')
    this.initBilling()
  },
  methods: {
    async initBilling () {
      if (!this.userInfo) {
        this.showMessage('warning', this.$t('billingPage.msg.loginRequired'))
        return
      }
      await this.fetchBillingData()
      if (this.isTeacher) {
        await this.fetchTeacherStudents()
      }
    },
    async fetchBillingData () {
      this.loading = true
      try {
        await Promise.all([
          this.fetchProducts(),
          this.fetchOrders()
        ])
        if (this.isTeacher) await this.fetchSeatUsage()
      } finally {
        this.loading = false
      }
    },
    async fetchProducts () {
      const result = await request.get({
        url: '/api/payment/products',
        params: {
          currency: this.currency
        }
      })
      if (result.code === 200) {
        this.products = result.data || []
        await this.fetchAnnualLicenseQuotes()
      } else {
        this.showMessage('error', result.message || this.$t('billingPage.msg.loadProductsFailed'))
      }
    },
    async fetchOrders () {
      const params = { page: 1, size: 20 }
      const result = await request.get({
        url: '/api/payment/orders',
        params
      })
      if (result.code === 200) {
        this.orders = result.data?.records || []
      } else {
        this.showMessage('error', result.message || this.$t('billingPage.msg.loadBillingHistoryFailed'))
      }
    },
    async fetchSeatUsage () {
      this.seatUsageLoading = true
      try {
        const result = await request.get({
          url: '/api/payment/annual-license/seat-usage'
        })
        if (result.code === 200) {
          this.seatUsage = result.data || null
        } else {
          this.seatUsage = null
          this.showMessage('error', result.message || this.$t('billingPage.msg.loadLicenseUsageFailed'))
        }
      } finally {
        this.seatUsageLoading = false
      }
    },
    async fetchAnnualLicenseQuotes () {
      const products = this.products.filter(product => product.productType === 'ANNUAL_LICENSE')
      await Promise.all(products.flatMap(product => {
        return this.annualLicenseTierProbeQuantities().map(quantity => this.fetchAnnualLicenseQuote(product.id, quantity))
      }))
    },
    async fetchAnnualLicenseQuote (productId, quantity = 1) {
      if (!productId) return
      this.quoteLoadingProductId = productId
      try {
        const result = await request.get({
          url: '/api/payment/annual-license/quote',
          params: {
            productId,
            quantity
          }
        })
        if (result.code === 200) {
          const quotes = this.annualTierQuotes[productId] || {}
          this.$set(this.annualTierQuotes, productId, {
            ...quotes,
            [quantity]: result.data
          })
          if (quantity === 1) {
            this.$set(this.annualQuotes, productId, result.data)
          }
          return result.data
        } else {
          this.showMessage('error', result.message || this.$t('billingPage.msg.loadQuoteFailed'))
        }
      } catch (error) {
        this.showMessage('error', error.message || this.$t('billingPage.msg.loadQuoteFailed'))
      } finally {
        this.quoteLoadingProductId = ''
      }
    },
    async fetchDialogQuote () {
      if (!this.selectedProduct?.id) return
      this.dialogQuoteLoading = true
      try {
        const result = await request.get({
          url: '/api/payment/annual-license/quote',
          params: {
            productId: this.selectedProduct.id,
            quantity: this.normalizedLicenseQuantity()
          }
        })
        if (result.code === 200) {
          this.dialogQuote = result.data
        } else {
          this.showMessage('error', result.message || this.$t('billingPage.msg.loadQuoteFailed'))
        }
      } catch (error) {
        this.showMessage('error', error.message || this.$t('billingPage.msg.loadQuoteFailed'))
      } finally {
        this.dialogQuoteLoading = false
      }
    },
    async fetchTeacherStudents () {
      if (!this.userInfo?.id) return
      this.studentsLoading = true
      try {
        const result = await request.post({
          url: `/api/guardian-rel/guardian/${this.userInfo.id}/type/1`
        })
        if (result.code === 200) {
          this.teacherStudents = result.data || []
        } else {
          this.showMessage('error', result.message || this.$t('billingPage.msg.loadLinkedStudentsFailed'))
        }
      } catch (error) {
        this.showMessage('error', error.message || this.$t('billingPage.msg.loadLinkedStudentsFailed'))
      } finally {
        this.studentsLoading = false
      }
    },
    async purchaseAnnualLicense (product) {
      this.actionLoadingProductId = product.id
      try {
        const result = await request.post({
          url: '/api/payment/direct-pay',
          params: {
            productId: product.id,
            quantity: this.normalizedLicenseQuantity()
          }
        })
        if (result.code !== 200) {
          throw new Error(result.message || this.$t('billingPage.msg.paymentFailed'))
        }
        if (result.data?.requiresAction) {
          await this.confirmRequiredPayment(result.data)
        }
        await this.fetchBillingData()
        if (this.isStudent) {
          await this.refreshCurrentStudentLicense()
        }
        this.showMessage('success', this.isTeacher ? this.$t('billingPage.msg.seatsPurchased') : this.$t('billingPage.msg.annualAccessActivated'))
        return true
      } catch (error) {
        const message = error.message || this.$t('billingPage.msg.paymentFailedBindCard')
        this.showMessage('error', message)
        return false
      } finally {
        this.actionLoadingProductId = ''
      }
    },
    async confirmRequiredPayment (payment) {
      if (!payment?.clientSecret) {
        throw new Error(this.$t('billingPage.msg.bankVerificationRequired'))
      }
      const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || ''
      if (!publishableKey) {
        throw new Error(this.$t('creditCardPage.msg.missingStripeKey'))
      }
      const Stripe = await this.loadStripeJs()
      const stripe = Stripe(publishableKey)
      const confirmation = await stripe.confirmCardPayment(payment.clientSecret)
      if (confirmation.error) {
        throw new Error(confirmation.error.message || this.$t('billingPage.msg.paymentFailed'))
      }
      if (confirmation.paymentIntent?.status !== 'succeeded') {
        throw new Error(this.$t('billingPage.msg.bankVerificationRequired'))
      }
      await this.waitForOrderPaid(payment.orderNo)
    },
    loadStripeJs () {
      if (window.Stripe) return Promise.resolve(window.Stripe)
      if (!stripeJsPromise) {
        stripeJsPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = STRIPE_JS_URL
          script.async = true
          script.onload = () => resolve(window.Stripe)
          script.onerror = () => reject(new Error(this.$t('creditCardPage.msg.stripeLoadFailed')))
          document.head.appendChild(script)
        })
      }
      return stripeJsPromise
    },
    async waitForOrderPaid (orderNo) {
      if (!orderNo) return
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const result = await request.get({
          url: `/api/payment/orders/${orderNo}/status`
        })
        const status = String(result.data?.status || '').toUpperCase()
        if (status === 'PAID') return
        if (status === 'FAILED' || status === 'EXPIRED') {
          throw new Error(result.data?.failureReason || this.$t('billingPage.msg.paymentFailed'))
        }
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      throw new Error(this.$t('billingPage.msg.paymentFailed'))
    },
    async refreshCurrentStudentLicense () {
      const result = await request.post({
        url: '/user/findById',
        params: {
          id: this.userInfo.id,
          role: this.userInfo.role
        }
      })
      if (result.code !== 200 || !result.data) return
      this.userInfo = {
        ...this.userInfo,
        ...result.data
      }
      this.$store.commit('user/SET_USER_INFO', this.userInfo)
    },
    openPurchaseDialog (product) {
      this.selectedProduct = product
      this.dialogQuote = null
      this.dialogQuantity = 1
      this.licenseQuantity = this.dialogQuantity
      this.purchaseDialogVisible = true
      this.fetchDialogQuote()
    },
    async confirmAnnualLicensePurchase () {
      if (!this.selectedProduct) return
      const paid = await this.purchaseAnnualLicense(this.selectedProduct)
      if (paid) {
        this.purchaseDialogVisible = false
      }
    },
    async activateStudentLicense (student) {
      if (!student?.studentId || !this.userInfo?.id) return
      this.activationLoadingStudentId = student.studentId
      try {
        const result = await request.post({
          url: `/api/guardian-rel/bind?guardianId=${this.userInfo.id}&guardianType=1&studentId=${student.studentId}&relation=${encodeURIComponent(student.relation || 'Teacher')}&operator=${encodeURIComponent(this.userInfo.username || this.userInfo.userAccount || 'teacher')}&activate=true`
        })
        if (result.code !== 200) {
          throw new Error(result.message || this.$t('billingPage.msg.activationFailed'))
        }
        this.showMessage('success', this.$t('billingPage.msg.activationSuccess'))
        await Promise.all([
          this.fetchTeacherStudents(),
          this.fetchSeatUsage()
        ])
      } catch (error) {
        this.showMessage('error', error.message || this.$t('billingPage.msg.activationFailed'))
      } finally {
        this.activationLoadingStudentId = ''
      }
    },
    setLicenseQuantity (quantity) {
      const normalized = Math.max(1, Number(quantity) || 1)
      this.licenseQuantity = normalized
      this.dialogQuantity = normalized
      this.handleQuantityChange()
    },
    handleQuantityChange () {
      const normalized = this.normalizedLicenseQuantity()
      this.licenseQuantity = normalized
      this.dialogQuantity = normalized
      if (this.purchaseDialogVisible) {
        this.fetchDialogQuote()
      } else {
        this.fetchAnnualLicenseQuotes()
      }
    },
    normalizedLicenseQuantity () {
      if (this.isStudent) return 1
      const quantity = this.purchaseDialogVisible ? this.dialogQuantity : this.licenseQuantity
      return Math.max(1, Math.floor(Number(quantity) || 1))
    },
    annualLicenseTierProbeQuantities () {
      return [1, 6, 31]
    },
    getAnnualTierQuote (productId, quantity) {
      return this.annualTierQuotes[productId]?.[quantity] || null
    },
    formatTierPrice (productId, quantity) {
      const quote = this.getAnnualTierQuote(productId, quantity)
      return quote ? this.formatMoney(quote.unitAmount, quote.currency) : this.$t('billingPage.loadingPrice')
    },
    formatBestSavings (productId) {
      const standardQuote = this.getAnnualTierQuote(productId, 1)
      const bestQuote = this.getAnnualTierQuote(productId, 31)
      if (!standardQuote || !bestQuote) return this.$t('billingPage.volumePricingBackend')
      const savings = Number(standardQuote.unitAmount || 0) - Number(bestQuote.unitAmount || 0)
      return savings > 0
        ? this.$t('billingPage.saveUpTo', { amount: this.formatMoney(savings, standardQuote.currency) })
        : this.$t('billingPage.volumePricingBackend')
    },
    formatTierNote (productId, quantity, fallback) {
      if (quantity === 1) return fallback
      const standardQuote = this.getAnnualTierQuote(productId, 1)
      const tierQuote = this.getAnnualTierQuote(productId, quantity)
      if (!standardQuote || !tierQuote) return fallback
      const savings = Number(standardQuote.unitAmount || 0) - Number(tierQuote.unitAmount || 0)
      return savings > 0 ? this.$t('billingPage.saveEach', { amount: this.formatMoney(savings, standardQuote.currency) }) : fallback
    },
    annualLicensePriceTiers (productId) {
      return [
        {
          min: 1,
          max: 5,
          label: this.$t('billingPage.tierOneLabel'),
          price: this.formatTierPrice(productId, 1),
          note: ''
        },
        {
          min: 6,
          max: 30,
          label: this.$t('billingPage.tierTwoLabel'),
          price: this.formatTierPrice(productId, 6),
          note: this.formatTierNote(productId, 6, this.$t('billingPage.volumePrice'))
        },
        {
          min: 31,
          max: null,
          label: this.$t('billingPage.tierThreeLabel'),
          price: this.formatTierPrice(productId, 31),
          note: this.formatTierNote(productId, 31, this.$t('billingPage.bestValue'))
        }
      ]
    },
    isStudentActivated (student) {
      return student?.licenseActivated === true ||
        String(student?.licenseActivated).toLowerCase() === 'true' ||
        String(student?.licenseStatus || '').toUpperCase() === 'ACTIVE'
    },
    findAnnualLicenseBasePrice (product) {
      const prices = Array.isArray(product.prices) ? product.prices : []
      return prices.find(price => price.status === 1 && !price.billingInterval && String(price.currency || '').toLowerCase() === this.currency)
    },
    localizedProductName (source = {}, fallbackId = '') {
      const isFrench = this.$i18n.locale === 'fr'
      const candidates = isFrench
        ? [source.nameFr, source.productNameFr, source.name, source.productName, source.nameEn, source.productNameEn, source.nameZh]
        : [source.nameEn, source.productNameEn, source.name, source.productName, source.nameZh, source.nameFr, source.productNameFr]
      return candidates.find(Boolean) || this.$t('billingPage.productFallback', { id: fallbackId })
    },
    localizedProductDescription (product = {}) {
      const isFrench = this.$i18n.locale === 'fr'
      const candidates = isFrench
        ? [product.descriptionFr, product.description, product.descriptionEn, product.descriptionZh]
        : [product.descriptionEn, product.description, product.descriptionZh, product.descriptionFr]
      return candidates.find(Boolean) || this.$t('billingPage.defaultProductDescription')
    },
    formatStudentMeta (student) {
      const fields = [
        student.studentEmail,
        student.studentGrade,
        student.relation
      ].filter(Boolean)
      return fields.join(' · ') || this.$t('billingPage.linkedStudentFallback')
    },
    formatMoney (amount, currency = 'usd') {
      const value = Number(amount || 0) / 100
      const currencyCode = String(currency || 'usd').toUpperCase()
      if (currencyCode === 'CAD') {
        const amountText = new Intl.NumberFormat(this.localeCode(), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value)
        return `CAD $${amountText}`
      }
      return new Intl.NumberFormat(this.localeCode(), {
        style: 'currency',
        currency: currencyCode
      }).format(value)
    },
    formatDate (date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString(this.localeCode(), {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    },
    localeCode () {
      return this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'
    },
    formatStudentFallback (studentId) {
      return this.$t('billingPage.studentFallback', { id: studentId })
    },
    formatActivatedText (student) {
      return student.licenseExpireAt
        ? this.$t('billingPage.activatedUntil', { date: this.formatDate(student.licenseExpireAt) })
        : this.$t('billingPage.activated')
    },
    formatOrderStatus (status) {
      const map = {
        PAID: this.$t('billingPage.statusPaid'),
        PENDING: this.$t('billingPage.statusPending'),
        CREATED: this.$t('billingPage.statusPending'),
        FAILED: this.$t('billingPage.statusFailed'),
        REFUNDED: this.$t('billingPage.statusRefunded'),
        PARTIALLY_REFUNDED: this.$t('billingPage.statusPartiallyRefunded'),
        EXPIRED: this.$t('billingPage.statusExpired')
      }
      return map[status] || status || '-'
    },
    getOrderStatusClass (status) {
      const map = {
        PAID: 'status-paid',
        PENDING: 'status-pending',
        CREATED: 'status-pending',
        REFUNDED: 'status-refunded',
        PARTIALLY_REFUNDED: 'status-refunded',
        FAILED: 'status-failed',
        EXPIRED: 'status-muted'
      }
      return map[status] || 'status-muted'
    },
    showMessage (type, message) {
      if (this.$message && this.$message[type]) {
        this.$message[type](message)
      } else {
        alert(message)
      }
    },
    goHome () {
      this.$router.push(this.getHomeRoute())
    },
    getHomeRoute () {
      if (this.userInfo?.role === 'S') return '/studentHomepage'
      if (this.userInfo?.type === 9) return '/teacherManagement'
      return '/parentOrTeacher'
    }
  }
}
</script>

<style scoped>
.billing-page {
  --sh-purple: #7c4dff;
  --sh-purple-deep: #5b21b6;
  --sh-orange: #ff8a00;
  --sh-lavender: #ede7f6;
  --sh-text-muted: #94a3b8;
  --sh-card-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: calc(100% - 90px);
  padding: 12px 16px;
  box-sizing: border-box;
}

.box {
  width: 100%;
  max-width: 1443px;
  border-radius: var(--sh-card-radius);
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
}

.hero-banner {
  border-radius: var(--sh-card-radius);
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 10px 28px rgba(91, 33, 182, 0.16);
  background: linear-gradient(120deg, rgb(79, 70, 229) 0%, rgb(124, 58, 237) 60%, rgb(139, 92, 246) 100%);
}

.hero-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 280px;
}

.hero-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
}

.hero-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.hero-tag {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #fff;
  background: rgba(255, 255, 255, 0.22);
  line-height: 1.2;
}

.hero-tag-active {
  background: linear-gradient(90deg, #34d399 0%, #16a34a 100%);
}

.hero-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
}

.hero-sub {
  margin: 5px 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
}

.hero-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.hero-summary {
  display: flex;
  gap: 0;
  flex-wrap: nowrap;
  border-radius: 12px;
  padding: 8px 6px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
}

.hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 76px;
  padding: 0 12px;
}

.hero-stat + .hero-stat {
  border-left: 1px solid rgba(255, 255, 255, 0.22);
}

.hero-stat-num {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
}

.hero-stat-label {
  margin-top: 3px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.hero-actions {
  display: flex;
  align-items: center;
}

.hero-home-btn {
  border: none;
  border-radius: 999px;
  height: 38px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #fb923c 0%, #f97316 100%);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.hero-home-btn i {
  font-size: 14px;
}

.main-content {
  width: 100%;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.summary-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.summary-label {
  display: block;
  font-size: 0.8125rem;
  color: #64748b;
}

.summary-card strong {
  display: block;
  margin-top: 6px;
  font-size: 1.3rem;
}

.summary-card p {
  margin: 4px 0 0;
  color: #64748b;
}

.card-section {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.9);
  margin-bottom: 20px;
}

.card-section h3 {
  margin: 0;
  font-size: 16px;
  color: #0f172a;
}

.empty-state {
  margin-top: 16px;
  padding: 18px;
  border: 1px dashed #c4b5fd;
  border-radius: 12px;
  color: #475569;
  background: #fbfaff;
}

.product-empty-state p {
  margin: 0 0 8px;
}

.product-section {
  background: transparent;
  box-shadow: none;
  border: none;
  padding: 0;
}

.section-pill-title {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.8rem !important;
  color: rgb(109, 40, 217) !important;
  border: 1px solid rgb(221, 214, 254);
  background: rgb(237, 233, 254);
}

.product-list-wrap {
  margin-top: 16px;
}

.annual-product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.annual-product-card {
  padding: 22px;
  border: 1px solid #e8ddff;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff 0%, #fbfaff 100%);
  box-shadow: 0 16px 40px rgba(88, 28, 135, 0.08);
}

.annual-card-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: flex-start;
}

.annual-card-copy {
  min-width: 0;
}

.annual-card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.annual-card-copy h4 {
  margin: 0;
  color: #111827;
  font-size: 20px;
  line-height: 1.25;
}

.annual-card-copy p {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.annual-price-hero {
  min-width: 170px;
  padding: 16px;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 62%, #10b981 100%);
  box-shadow: 0 14px 28px rgba(91, 33, 182, 0.22);
}

.annual-price-hero span,
.annual-price-hero small {
  display: block;
  color: rgba(255, 255, 255, 0.86);
  font-size: 12px;
  font-weight: 700;
}

.annual-price-hero strong {
  display: block;
  margin: 5px 0 4px;
  color: #fff;
  font-size: 27px;
  line-height: 1;
}

.annual-tier-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;
}

.annual-tier-card {
  display: grid;
  gap: 5px;
  min-height: 92px;
  padding: 12px;
  border: 1px solid #ebe5ff;
  border-radius: 13px;
  background: #fff;
}

.annual-tier-card span {
  color: #7c3aed;
  font-size: 12px;
  font-weight: 800;
}

.annual-tier-card strong {
  color: #111827;
  font-size: 18px;
  line-height: 1.1;
}

.annual-tier-card small {
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

.annual-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #ede9fe;
}

.annual-card-footer span {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.product-type-badge,
.product-status-badge {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: 800;
}

.product-type-badge {
  color: #6d28d9;
  background: #f0eaff;
  border: 1px solid #ddd6fe;
}

.product-status-badge {
  color: #15803d;
  background: #ecfdf3;
}

.quantity-control {
  display: inline-grid;
  grid-template-columns: 34px 62px 34px;
  align-items: center;
  overflow: hidden;
  border: 1px solid #e5ddff;
  border-radius: 999px;
  background: #fbfaff;
}

.quantity-control button,
.quantity-control input {
  height: 36px;
  border: none;
  background: transparent;
  color: #6d28d9;
  font-weight: 800;
}

.quantity-control button {
  cursor: pointer;
  font-size: 16px;
}

.quantity-control button:disabled {
  cursor: not-allowed;
  color: #c4b5fd;
}

.quantity-control input {
  width: 62px;
  text-align: center;
  border-left: 1px solid #eee7ff;
  border-right: 1px solid #eee7ff;
  color: #111827;
  outline: none;
}

.quantity-control input::-webkit-inner-spin-button,
.quantity-control input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.quantity-fixed {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 34px;
  border-radius: 999px;
  color: #111827;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  font-weight: 800;
}

.product-action-btn {
  min-width: 180px;
  height: 40px;
  border: 1px solid #e0d8ff;
  border-radius: 999px;
  cursor: pointer;
  background: #f8f7ff;
  color: #7c3aed;
  font-weight: 800;
  font-size: 0.92rem;
}

.product-action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.annual-buy-btn {
  min-width: 146px;
  height: 42px;
  padding: 0 18px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff !important;
  background: linear-gradient(90deg, #5b21b6 0%, #6d28d9 58%, #4338ca 100%);
  box-shadow: 0 10px 22px rgba(91, 33, 182, 0.26);
  text-shadow: 0 1px 1px rgba(49, 17, 109, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.annual-buy-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(91, 33, 182, 0.32);
}

.annual-buy-btn span {
  color: #fff;
  font-size: 0.95rem;
  line-height: 1;
  white-space: nowrap;
}

.annual-buy-btn i {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6d28d9;
  background: rgba(255, 255, 255, 0.92);
  font-weight: 800;
}

.annual-buy-btn:disabled {
  transform: none;
  box-shadow: none;
}

.purchase-dialog-body {
  display: grid;
  gap: 16px;
}

.dialog-product-head {
  display: grid;
  gap: 8px;
}

.dialog-product-head h4 {
  margin: 0;
  color: #111827;
  font-size: 20px;
}

.dialog-product-head p {
  margin: 0;
  color: #64748b;
  line-height: 1.45;
}

.dialog-quantity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  border: 1px solid #eee7ff;
  border-radius: 14px;
  background: #fbfaff;
}

.dialog-quantity-row > div:first-child {
  display: grid;
  gap: 4px;
}

.dialog-quantity-row strong {
  color: #111827;
}

.dialog-quantity-row span {
  color: #64748b;
  font-size: 13px;
}

.purchase-quantity-control {
  flex: 0 0 auto;
}

.dialog-quote-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  overflow: hidden;
  border: 1px solid #ddd6fe;
  border-radius: 15px;
  background: #ddd6fe;
}

.dialog-quote-card > div {
  display: grid;
  gap: 6px;
  padding: 16px;
  background: #fff;
}

.dialog-quote-card span {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.dialog-quote-card strong {
  color: #111827;
  font-size: 22px;
}

.dialog-tier-row {
  margin-top: 0;
}

.dialog-tier-row--single {
  grid-template-columns: 1fr;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-secondary-btn {
  min-width: 96px;
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  cursor: pointer;
  color: #475569;
  background: #fff;
  font-weight: 800;
}

.dialog-primary-btn {
  min-width: 120px;
}

::v-deep .annual-purchase-dialog {
  border-radius: 16px;
  overflow: hidden;
}

::v-deep .annual-purchase-dialog .el-dialog__header {
  padding: 20px 24px 12px;
}

::v-deep .annual-purchase-dialog .el-dialog__title {
  color: #111827;
  font-weight: 800;
}

::v-deep .annual-purchase-dialog .el-dialog__body {
  padding: 12px 24px 18px;
}

::v-deep .annual-purchase-dialog .el-dialog__footer {
  padding: 0 24px 22px;
}

.teacher-students-section {
  background: transparent;
  box-shadow: none;
  border: none;
  padding: 0;
}

.license-usage-list {
  display: grid;
  gap: 12px;
}

.license-usage-card {
  padding: 16px 18px;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  background: #fff;
}

.license-usage-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.license-usage-head > div {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.license-usage-head strong {
  color: #6d28d9;
  font-size: 15px;
  font-weight: 800;
}

.license-usage-head span {
  overflow: hidden;
  color: #64748b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.license-usage-pill {
  flex: 0 0 auto;
  padding: 6px 11px;
  border-radius: 999px;
  color: #15803d !important;
  background: #ecfdf3;
  font-size: 12px !important;
  font-weight: 800;
}

.activated-students-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.activated-student-chip {
  padding: 5px 10px;
  border-radius: 999px;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 700;
}

.license-usage-empty {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 13px;
}

.student-license-list {
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #eef1f6;
}

.student-license-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #edf1f6;
}

.student-license-row:last-child {
  border-bottom: none;
}

.student-license-row.activated {
  background: #fbfffd;
}

.student-license-main {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.student-license-main strong {
  color: #111827;
  font-size: 15px;
  font-weight: 800;
}

.student-license-main span {
  overflow: hidden;
  color: #64748b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-activated-badge {
  min-width: 130px;
  padding: 7px 12px;
  border-radius: 12px;
  display: grid;
  gap: 2px;
  text-align: center;
  color: #15803d;
  background: #ecfdf3;
  font-size: 12px;
  font-weight: 800;
}

.student-activated-badge span,
.student-activated-badge small {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-activated-badge small {
  color: #16a34a;
  font-size: 10px;
  font-weight: 700;
}

.payment-layout {
  display: flex;
  justify-content: space-between;
  gap: 0;
  align-items: start;
}

.payment-layout > .payment-preview{
  width: 40%;
}
.payment-layout > .payment-form-wrap {
  width: 58%;
}

.payment-section {
  background: transparent;
  box-shadow: none;
  border: none;
  padding: 0;
}

.payment-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.payment-subtitle {
  margin: 0;
  font-size: 16px;
  color: #c6c3d4;
}

.payment-preview p{
  color: #fff;
}
.payment-form-wrap p {
  color: #64748b;
}

.card-preview {
  border-radius: 14px;
  padding: 36px;
  background: linear-gradient(135deg, #4938ff 0%, #6d4aff 100%);
  box-shadow: 0 10px 28px rgba(73, 56, 255, 0.28);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.card-preview::after {
  content: '';
  position: absolute;
  right: -40px;
  top: -30px;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.card-logo {
  position: absolute;
  right: 30px;
  top: 36px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #fff;
}

.chip {
  display: inline-block;
  width: 44px;
  height: 30px;
  border-radius: 7px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.card-number {
  margin: 26px 0 24px;
  font-size: 22px;
  letter-spacing: 0.22em;
  font-weight: 700;
}

.preview-meta {
  display: flex;
  justify-content: space-between;
}

.preview-meta small {
  color: rgba(255, 255, 255, 0.7);
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.preview-meta strong {
  font-size: 16px;
  line-height: 1.2;
}

.secure-card {
  margin-top: 14px;
  border-radius: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #edf0f7;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.secure-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #10b981;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  margin-top: 2px;
}

.secure-text {
  margin: 0 0 3px;
  font-weight: 700;
  color: #0f172a !important;
  font-size: 1rem;
}

.helper-text {
  color: #64748b;
  line-height: 1.45;
  font-size: 12px;
  font-weight: 600;
}

.payment-form-wrap {
  border-radius: 14px;
  padding: 16px 18px !important;
}

.form-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-head h3 {
  margin: 0;
}

.form-head p {
  margin: 2px 0 0;
}

.form-head-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #5b4bff;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.payment-form {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-form label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #000;
}

.payment-form input,
.payment-form select {
  border: 1px solid #e4e8f5;
  border-radius: 12px;
  padding: 11px 12px;
  font-size: 0.95rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.expiry-field {
  grid-column: span 2;
}

.expiry-field .form-inner-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.bind-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bind-btn {
  flex: 1;
}

.bind-btn-disabled {
  background: #f3f4f6 !important;
  color: #c0c4cf !important;
  box-shadow: none;
}

.card-action-btn {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid #e6e8ef;
  background: #fff;
  color: #9ca3af;
  cursor: pointer;
}

.accepted-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
}

.accepted-brand {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}

.accepted-brand.visa {
  color: #6366f1;
}

.bind-btn,
.support-btn {
  border: none;
  border-radius: 999px;
  padding: 11px 14px;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(90deg, #7c4dff 0%, #5b21b6 100%);
  color: #fff;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  margin-bottom: 12px;
}

.section-title-row span {
  color: #64748b;
}

.billing-history-section {
  background: transparent;
  box-shadow: none;
  border: none;
  padding: 0;
}

.billing-count {
  font-size: 16px;
  color: #a3a0b6 !important;
}

.table-wrap {
  overflow-x: auto;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #eef1f6;
}

.billing-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 780px;
}

.billing-table th,
.billing-table td {
  text-align: left;
  padding: 16px 14px;
  border-bottom: 1px solid #edf1f6;
  font-size: 0.98rem;
}

.billing-table th {
  color: #b2b1bf;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.82rem;
}

.billing-table tbody tr:last-child td {
  border-bottom: none;
}

.empty-table-cell {
  text-align: center;
  color: #64748b;
}

.invoice-id {
  color: #6d28d9;
  font-weight: 600;
}

.amount {
  color: #111827;
  font-weight: 700;
}

.status-tag {
  background: #ecfdf3;
  color: #15803d;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-paid {
  background: #ecfdf3;
  color: #15803d;
}

.status-pending {
  background: #fff7ed;
  color: #c2410c;
}

.status-refunded {
  background: #eff6ff;
  color: #1d4ed8;
}

.status-failed {
  background: #fef2f2;
  color: #b91c1c;
}

.status-muted {
  background: #f1f5f9;
  color: #475569;
}

.support p {
  margin: 8px 0 14px;
  color: #64748b;
}

@media (max-width: 1100px) {
  .hero-title {
    font-size: 32px;
  }

  .hero-right {
    width: 100%;
    justify-content: space-between;
    margin-left: 0;
  }

  .summary-grid,
  .payment-layout {
    grid-template-columns: 1fr;
  }

  .payment-layout {
    display: grid;
    gap: 12px;
  }

  .payment-layout > .payment-preview,
  .payment-layout > .payment-form-wrap {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .box {
    padding: 12px 0;
  }

  .hero-main,
  .license-usage-head,
  .student-license-row {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-left,
  .hero-right,
  .hero-summary,
  .hero-actions,
  .hero-home-btn,
  .student-license-row .product-action-btn {
    width: 100%;
  }

  .hero-right {
    gap: 10px;
  }

  .hero-summary {
    justify-content: center;
  }

  .license-usage-pill,
  .student-activated-badge {
    width: 100%;
  }

  .section-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .annual-card-main,
  .annual-card-footer,
  .dialog-quantity-row,
  .dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .annual-card-main,
  .annual-tier-row,
  .dialog-quote-card {
    grid-template-columns: 1fr;
  }

  .annual-price-hero,
  .product-action-btn,
  .dialog-secondary-btn,
  .dialog-primary-btn {
    width: 100%;
  }

  .student-license-main span {
    white-space: normal;
  }
}
</style>
